import React, { useState, useEffect } from 'react';
import axios from '../axiosConfig';

/**
 * USCS 라벨링 모달
 *
 * 선택된 시추공들의 SPT 데이터에 USCS 지반 분류 라벨을 할당하는 UI
 */
const USCSLabelingModal = ({ selectedBoreholes, onClose, onComplete }) => {
  const [boreholes, setBoreholes] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedRows, setSelectedRows] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // USCS 옵션
  const uscsOptions = [
    { label: '암반', value: 'WR' },
    { label: '자갈', value: 'GW' },
    { label: '모래', value: 'SM' },
    { label: '실트', value: 'ML' },
    { label: '점토', value: 'CL' },
    { label: '유기질토', value: 'OH' },
    { label: '기타', value: 'ET' }
  ];

  useEffect(() => {
    fetchBoreholeDetails();
  }, []);

  const fetchBoreholeDetails = async () => {
    try {
      setLoading(true);

      // 각 시추공의 상세 정보를 가져옴
      const promises = selectedBoreholes.map(boreholeId =>
        axios.get(`/boreholes/${boreholeId}`).then(res => res.data.data)
      );

      const results = await Promise.all(promises);

      // layer 경계 SPT만 필터링 (n=null, d=null) + 실제 SPT가 있지만 layer가 없는 경우 첫 번째 SPT
      const processedBoreholes = results.map(borehole => {
        const sortedSpts = [...borehole.spts].sort((a, b) => b.elevation - a.elevation);
        const layerBoundaries = sortedSpts.filter(spt => spt.n === null && spt.d === null);
        const groundElevation = Math.max(...sortedSpts.map(s => s.elevation));

        let targetSpts;
        const realSpts = sortedSpts.filter(spt => spt.n !== null && spt.d !== null);

        if (layerBoundaries.length === 0) {
          // layer 경계가 없으면 실제 SPT 중 첫 번째
          targetSpts = realSpts.length > 0 ? [realSpts[0]] : [];
        } else {
          // 실제 SPT 데이터가 있는 layer만 포함하고, 두께가 양수인 것만 포함
          targetSpts = layerBoundaries.filter((spt, idx) => {
            // start_depth = 현재 layer의 depth
            const startDepth = groundElevation - spt.elevation;

            // end_depth = 다음 layer boundary의 depth (없으면 가장 깊은 실제 SPT의 depth)
            let endDepth;
            const nextSpt = layerBoundaries[idx + 1];
            if (nextSpt) {
              endDepth = groundElevation - nextSpt.elevation;
            } else {
              // 마지막 layer: 실제 SPT 중 가장 낮은 elevation
              if (realSpts.length > 0) {
                const minRealElevation = Math.min(...realSpts.map(s => s.elevation));
                endDepth = groundElevation - minRealElevation;
              } else {
                endDepth = startDepth;
              }
            }

            const thickness = endDepth - startDepth;

            if (thickness <= 0) {
              return false;
            }

            // 이 layer 범위 내에 실제 SPT 데이터가 있는지 확인
            const layerEndElevation = groundElevation - endDepth;
            const sptsInLayer = realSpts.filter(s => s.elevation <= spt.elevation && s.elevation >= layerEndElevation);

            return sptsInLayer.length > 0; // 실제 SPT 데이터가 있는 layer만 포함
          });
        }

        return {
          ...borehole,
          targetSpts
        };
      });

      setBoreholes(processedBoreholes);
    } catch (error) {
      console.error('Error fetching borehole details:', error);
      alert('시추공 정보를 불러오는데 실패했습니다.');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleUSCSChange = (boreholeId, sptId, value) => {
    setBoreholes(prevBoreholes =>
      prevBoreholes.map(borehole =>
        borehole.id === boreholeId
          ? {
              ...borehole,
              targetSpts: borehole.targetSpts.map(spt =>
                spt.id === sptId ? { ...spt, uscs_label: value } : spt
              )
            }
          : borehole
      )
    );
  };

  const toggleRowSelection = (boreholeId, sptId) => {
    const key = `${boreholeId}-${sptId}`;
    setSelectedRows(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleBatchLabel = (uscsValue) => {
    const currentBorehole = boreholes[activeTab];
    if (!currentBorehole) return;

    // 선택된 행들만 업데이트
    const updatedTargetSpts = currentBorehole.targetSpts.map(spt => {
      const key = `${currentBorehole.id}-${spt.id}`;
      if (selectedRows[key]) {
        return { ...spt, uscs_label: uscsValue };
      }
      return spt;
    });

    setBoreholes(prevBoreholes =>
      prevBoreholes.map((borehole, idx) =>
        idx === activeTab ? { ...borehole, targetSpts: updatedTargetSpts } : borehole
      )
    );

    // 선택 해제
    setSelectedRows({});
  };

  const validateLabels = () => {
    for (const borehole of boreholes) {
      for (const spt of borehole.targetSpts) {
        if (!spt.uscs_label) {
          return {
            valid: false,
            message: `시추공 "${borehole.name}"의 Layer ${spt.layer_index}에 USCS 라벨이 없습니다.`
          };
        }
      }
    }
    return { valid: true };
  };

  const handleSubmit = async () => {
    // 검증
    const validation = validateLabels();
    if (!validation.valid) {
      alert(validation.message);
      return;
    }

    try {
      setSubmitting(true);

      // 모든 업데이트 수집
      const updates = [];
      boreholes.forEach(borehole => {
        borehole.targetSpts.forEach(spt => {
          updates.push({
            spt_id: spt.id,
            uscs_label: spt.uscs_label
          });
        });
      });

      // API 호출
      await axios.patch('/spts/uscs-labels', { updates });

      onComplete();
    } catch (error) {
      console.error('Error updating USCS labels:', error);
      alert('USCS 라벨 저장에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[2000]">
        <div className="bg-white rounded-lg p-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p>데이터를 불러오는 중...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[2000] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-800">USCS 지반 분류 라벨링</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
            >
              ×
            </button>
          </div>
          <p className="text-sm text-gray-600 mt-2">
            각 Layer에 USCS 지반 분류를 지정하세요. 모든 Layer에 라벨이 필요합니다.
          </p>
        </div>

        {/* Tabs */}
        <div className="px-6 py-3 border-b border-gray-200 overflow-x-auto">
          <div className="flex space-x-2">
            {boreholes.map((borehole, index) => (
              <button
                key={borehole.id}
                onClick={() => setActiveTab(index)}
                className={`px-4 py-2 rounded-t-lg font-medium whitespace-nowrap transition-colors ${
                  activeTab === index
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {borehole.name}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {boreholes[activeTab] && (
            <div>
              {/* Batch Labeling Toolbar */}
              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-sm font-medium text-gray-700 mb-2">
                  일괄 라벨링 (선택된 행에 적용)
                </h3>
                <div className="flex flex-wrap gap-2">
                  {uscsOptions.map(option => (
                    <button
                      key={option.value}
                      onClick={() => handleBatchLabel(option.value)}
                      className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
                      disabled={Object.keys(selectedRows).filter(k => selectedRows[k]).length === 0}
                    >
                      {option.label} ({option.value})
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {Object.keys(selectedRows).filter(k => selectedRows[k]).length}개 행 선택됨
                </p>
              </div>

              {/* Layer Table */}
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-300 px-4 py-2 w-12">
                        <input
                          type="checkbox"
                          onChange={(e) => {
                            const allKeys = boreholes[activeTab].targetSpts.map(
                              spt => `${boreholes[activeTab].id}-${spt.id}`
                            );
                            if (e.target.checked) {
                              const newSelected = {};
                              allKeys.forEach(k => newSelected[k] = true);
                              setSelectedRows(newSelected);
                            } else {
                              setSelectedRows({});
                            }
                          }}
                        />
                      </th>
                      <th className="border border-gray-300 px-4 py-2">Layer</th>
                      <th className="border border-gray-300 px-4 py-2">시작 심도 (m)</th>
                      <th className="border border-gray-300 px-4 py-2">끝 심도 (m)</th>
                      <th className="border border-gray-300 px-4 py-2">두께 (m)</th>
                      <th className="border border-gray-300 px-4 py-2">USCS 라벨</th>
                    </tr>
                  </thead>
                  <tbody>
                    {boreholes[activeTab].targetSpts.map((spt, idx) => {
                      const rowKey = `${boreholes[activeTab].id}-${spt.id}`;

                      // 심도 계산: 지표면(최대 elevation)으로부터의 깊이
                      const allSpts = boreholes[activeTab].targetSpts;
                      const allBoreholesSpts = boreholes[activeTab].spts;
                      const groundElevation = Math.max(...allBoreholesSpts.map(s => s.elevation));
                      const realSpts = allBoreholesSpts.filter(s => s.n !== null && s.d !== null);

                      // start_depth = 현재 layer의 depth
                      const startDepth = groundElevation - spt.elevation;

                      // end_depth = 다음 layer boundary의 depth (없으면 가장 깊은 실제 SPT의 depth)
                      let endDepth;
                      const nextSpt = allSpts[idx + 1];
                      if (nextSpt) {
                        endDepth = groundElevation - nextSpt.elevation;
                      } else {
                        // 마지막 layer: 실제 SPT 중 가장 낮은 elevation
                        if (realSpts.length > 0) {
                          const minRealElevation = Math.min(...realSpts.map(s => s.elevation));
                          endDepth = groundElevation - minRealElevation;
                        } else {
                          endDepth = startDepth;
                        }
                      }

                      const thickness = endDepth - startDepth;

                      return (
                        <tr key={spt.id} className={selectedRows[rowKey] ? 'bg-blue-50' : ''}>
                          <td className="border border-gray-300 px-4 py-2 text-center">
                            <input
                              type="checkbox"
                              checked={selectedRows[rowKey] || false}
                              onChange={() => toggleRowSelection(boreholes[activeTab].id, spt.id)}
                            />
                          </td>
                          <td className="border border-gray-300 px-4 py-2 text-center">
                            {idx + 1}
                          </td>
                          <td className="border border-gray-300 px-4 py-2 text-center">
                            {startDepth.toFixed(2)}
                          </td>
                          <td className="border border-gray-300 px-4 py-2 text-center">
                            {endDepth.toFixed(2)}
                          </td>
                          <td className="border border-gray-300 px-4 py-2 text-center">
                            {thickness.toFixed(2)}
                          </td>
                          <td className="border border-gray-300 px-4 py-2">
                            <select
                              value={spt.uscs_label || ''}
                              onChange={(e) =>
                                handleUSCSChange(boreholes[activeTab].id, spt.id, e.target.value)
                              }
                              className={`w-full px-2 py-1 border rounded ${
                                !spt.uscs_label ? 'border-red-300 bg-red-50' : 'border-gray-300'
                              }`}
                            >
                              <option value="">선택하세요</option>
                              {uscsOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                  {option.label} ({option.value})
                                </option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            disabled={submitting}
          >
            취소
          </button>
          <button
            onClick={handleSubmit}
            className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:bg-gray-300"
            disabled={submitting}
          >
            {submitting ? '저장 중...' : '저장 및 분석 시작'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default USCSLabelingModal;
